export const AgentMessageTag = {
    responseMode: 'response_mode',
    pageContext: 'page_context',
    dataSources: 'data_sources',
    attachedLogs: 'attached_logs',
    attachedExceptions: 'attached_exceptions',
    attachedConversation: 'attached_conversation',
    // APM Layer-2 attachments (ORIONIQ-1384): whole, count-capped datasets the user attaches from an APM CTA.
    attachedOperations: 'attached_operations',
    attachedSpans: 'attached_spans',
    attachedInfra: 'attached_infra',
    // K8s 360 Layer-2 attachment (ORIONIQ-1166): the ONE item the user attached. Its Kubernetes kind is a FIELD of
    // the block, not a tag of its own — seven kinds describe one concept, and every tag has to be known by the
    // backend and mirrored to Artemis.
    attachedK8sItem: 'attached_k8s_item',
    // The Perses dashboard the user is viewing, supplied with the request so the agent reads it instead of
    // re-fetching it (ORIONIQ-1167). Not an `attached_*`: nothing was attached, it IS the page.
    dashboardDefinition: 'dashboard_definition',
    userQuestion: 'user_question',
};
const SECTION_SEPARATOR = '\n\n';
export const wrapAgentMessageSection = (tag, content) => `<${tag}>\n${content}\n</${tag}>`;
export const wrapUserQuestion = (message) => wrapAgentMessageSection(AgentMessageTag.userQuestion, message);
export const composeAgentMessage = (...parts) => parts.filter(Boolean).join(SECTION_SEPARATOR);
const sectionContent = (tag, message) => {
    const open = `<${tag}>`;
    const openIndex = message.indexOf(open);
    if (openIndex === -1)
        return undefined;
    const contentStart = openIndex + open.length;
    // lastIndexOf: the section wraps the whole trailing message, so its real close is the LAST one — a close tag
    // the user typed inside their own message can't truncate the recovered text.
    const closeIndex = message.lastIndexOf(`</${tag}>`);
    return closeIndex < contentStart ? undefined : message.slice(contentStart, closeIndex).trim();
};
// Strip a leading <tag>…</tag> preamble: from the start through the first blank line at/after the close tag.
// Cleans both an enclosing section and a legacy self-closed marker, independent of the wording inside. With no
// trailing blank line the opening tag is the user's own text (not a preamble) → leave it untouched.
const stripLeadingSection = (tag, message) => {
    if (!message.startsWith(`<${tag}>`))
        return message;
    const close = `</${tag}>`;
    const closeIndex = message.indexOf(close);
    if (closeIndex === -1)
        return message;
    const separatorIndex = message.indexOf(SECTION_SEPARATOR, closeIndex + close.length);
    return separatorIndex === -1 ? message : message.slice(separatorIndex + SECTION_SEPARATOR.length);
};
export const stripMessagePreambles = (message) => sectionContent(AgentMessageTag.userQuestion, message) ?? stripLeadingSection(AgentMessageTag.responseMode, message);
// --- Response-mode (Fast/Advanced) directive (ORIONIQ-1104/1231) --------------------------------------------------
// Shared HERE (not in a consumer) so the FE, orioniq-be, and history read-back all agree on ONE contract. 'fast' is an
// advisory directive prose wrapped in a <response_mode> section; 'advanced' sends nothing (passthrough). Before
// ORIONIQ-1216 the FE prepended this to the outgoing `message`; after 1216 orioniq-be sends it in
// `context.systemReminder`. Either way the <response_mode> ENVELOPE is the stable contract that lets
// `getResponseMode`/`stripMessagePreambles` recover the mode from a persisted message. Wording is provisional
// (advisory, no backend enforcement) — tune with the AI team.
const FAST_MODE_DIRECTIVES = [
    'fast',
    'Answer in FAST mode — optimize for time-to-answer over completeness. Follow these constraints:',
    '- Run a flat, single-pass investigation. Do NOT create a plan or a todo list, and do NOT run iterative or ' +
        'follow-up drill-downs.',
    '- Investigate only the single most relevant telemetry domain for this question (logs OR metrics OR traces). ' +
        'Do not fan out across domains or delegate to multiple sub-investigations unless the user explicitly asked ' +
        'for a cross-domain root-cause analysis.',
    '- Use as few queries as possible (prefer one), and issue any independent queries in parallel rather than ' +
        'sequentially. Do not re-query to chase completeness.',
    '- Do NOT produce a structured root-cause report (root cause, causation chain, key findings, ' +
        'recommendations). Give a short, direct, actionable answer grounded in what you retrieved.',
    '- If the question genuinely requires deep multi-domain root-cause analysis, say so in one sentence and ' +
        'offer to continue in Advanced mode instead of performing it now.',
].join('\n');
export const FAST_MODE_PREAMBLE = wrapAgentMessageSection(AgentMessageTag.responseMode, FAST_MODE_DIRECTIVES);
// The directive string App-AI puts in `context.systemReminder` for a turn — the wrapped <response_mode> block for
// 'fast', or undefined for 'advanced' (nothing to send). (ORIONIQ-1216.)
export const buildResponseModeDirective = (mode) => mode === 'fast' ? FAST_MODE_PREAMBLE : undefined;
// Recover the mode a persisted/incoming message was sent in: a <response_mode> section present → 'fast', else
// 'advanced' (Advanced never adds one). Keys on the tag (wording-independent) — the read counterpart of
// `stripMessagePreambles`. Used by the history adapters as the fallback source when no structured `mode` is stored.
export const getResponseMode = (message) => sectionContent(AgentMessageTag.responseMode, message) === undefined ? 'advanced' : 'fast';
//# sourceMappingURL=agent-message.js.map